import React, { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import { Fields, validationSchema } from './validation';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import { getEnabledCurrencies } from '@/utils/data-helpers/currecies';
import { UpdateContractAdminDataInputType } from '@/graphql/contracts/_generated_/contracts';
import { FormattingUtils } from '@/utils/Formatting.utils';
import useGetClientDatabases from '../hooks/useGetClientDatatbases';

interface IFormProps {
  onSubmit: (contractData: UpdateContractAdminDataInputType) => void
  onClose: () => void
  contract: ContractCommonData;
}

export default function EditContractHeadersModal({
  onSubmit,
  onClose,
  contract,
}: IFormProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const currencies = getEnabledCurrencies();
  const { databaseList, isDatabaseListLoading } = useGetClientDatabases();
  const contractDatabaseID = contract.clientDatabase?.clientDatabaseReference
    ?? (contract?.clientDatabaseReference || '');

  const getDatabasesWithCurrentOne = useMemo(() => {
    if (contractDatabaseID) {
      return databaseList?.find((el) => el?.iD === contractDatabaseID) ? databaseList
        : [...databaseList, { name: contractDatabaseID, iD: contractDatabaseID }];
    }
    return databaseList;
  }, [contractDatabaseID, databaseList]);

  const selectOrNullify = (oldValue: string, newValue: string) => {
    return oldValue === newValue ? null : newValue;
  };

  return (
    <>
      <AgModalWindow
        isOpen={!isDatabaseListLoading}
        onClose={onClose}
        title="Edit Contract Header"
        contentStyle={{ width: 340 }}
      >
        <Formik<Fields>
          initialValues={{
            contractNumber: contract.contractNumber,
            currency: contract.currency.currency,
            clientDatabaseID: contractDatabaseID
          }}
          validationSchema={validationSchema}
          onSubmit={() => setShowConfirmationModal(true)}
          validateOnMount={true}
        >
          {({ values, errors, touched, isValid, dirty, setFieldValue, setFieldTouched }) => {
            const currencyChanged = contract.currency.currency !== values.currency;
            return (
              <Form>
                <AgConfirmationModal
                  isOpen={showConfirmationModal}
                  title="You are about to modify contract header"
                  onClose={() => setShowConfirmationModal(false)}
                  onConfirm={() => {
                    onSubmit({
                      contractID: contract.contractID,
                      clientDatabaseID: selectOrNullify(contractDatabaseID, values.clientDatabaseID),
                      contractNumber: selectOrNullify(contract.contractNumber, values.contractNumber),
                      currency: selectOrNullify(contract.currency.currency, values.currency),
                    });
                    setShowConfirmationModal(false);
                  }}
                  contentStyle={{ width: 650 }}
                >
                  Once the changes are applied, non-consolidated contracts Failed SOs will be re-run again. <br />
                  In case the contract is a part of consolidated billing, the SO will remain in Failed status,
                  whenever Currency and/or Database were edited. <br />
                  {currencyChanged &&
                    <>Because currency has been modified, all current purchase orders will be unassigned.<br /> </>}
                  Please review Consolidate Billing setting to ensure correct SOs being generated.
                </AgConfirmationModal>
                <div className="mb-15">
                  <ModalFormTextInput
                    id="contractNumber"
                    labelTitle="Contract Number"
                    value={values.contractNumber ?? undefined}
                    isFloatingValidation={true}
                    onChange={(e) => {
                      setFieldTouched('contractNumber', true, false);
                      setFieldValue('contractNumber',
                        FormattingUtils.replaceControlCharacters(e.target.value)
                          ? e.target.value
                          : null);
                    }}
                    validationMessage={
                      (errors.contractNumber && touched.contractNumber) ? errors.contractNumber : undefined
                    }
                  />
                </div>
                <div className="mb-15">
                <ModalFormDropdown<ClientDatabaseType>
                    id="currency"
                    labelTitle="Currency"
                    optionsData={currencies}
                    getOptionLabel={(option) => option.formattedCurrency}
                    getOptionValue={(option) => option.currency}
                    defaultValue={currencies.find((el) => el?.numericCode === contract.currency.numericCode)}
                    menuPortalTarget={document.querySelector('body')}
                    isFloatingValidation={true}
                    isSearchable={true}
                    handleChange={(newValue) => {
                      setFieldTouched('currency', true, false);
                      setFieldValue('currency', newValue?.currency);
                    }}
                    validationMessage={
                      (errors?.currency && touched.currency) ? errors.currency : undefined
                    }
                  />
                </div>
              <ModalFormDropdown<ClientDatabaseType>
                  id="clientDatabaseID"
                  labelTitle="Database"
                  optionsData={getDatabasesWithCurrentOne}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.iD}
                  defaultValue={getDatabasesWithCurrentOne.find((el) => el?.iD === contractDatabaseID)}
                  isSearchable={true}
                  isFloatingValidation={true}
                  menuPortalTarget={document.querySelector('body')}
                  handleChange={(newValue) => {
                    setFieldTouched('clientDatabaseID', true, false);
                    setFieldValue('clientDatabaseID', newValue?.iD);
                  }}
                  isLoading={isDatabaseListLoading}
                  validationMessage={
                    (errors?.clientDatabaseID && touched.clientDatabaseID) ? errors.clientDatabaseID : undefined
                  }
                />

                <ActionButtonsWrapper
                  leftButton={(
                    <Button
                      buttonStyleType={ButtonStyleType.SECONDARY}
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  )}
                  rightButton={(
                    <Button
                      disabled={!isValid || !dirty}
                      buttonStyleType={ButtonStyleType.PRIMARY}
                      type="submit"
                    >
                      Save
                    </Button>
                  )}
                />
              </Form>
            );
          }}
        </Formik>
      </AgModalWindow>
    </>
  );
}